export interface StorageClassOption {
  value: string;
  label: string;
  description: string;
  costHint?: string;
  warning?: string;
}

export const STORAGE_CLASSES: readonly StorageClassOption[] = [
  {
    value: 'STANDARD',
    label: 'S3 Standard',
    description: 'Best for files you access regularly. No minimum storage duration.',
  },
  {
    value: 'STANDARD_IA',
    label: 'Standard – Infrequent Access',
    description: 'Lower cost for files rarely accessed. Minimum 30-day storage, 128 KB minimum charge.',
    costHint: 'Minimum 30-day storage, 128 KB minimum object charge',
  },
  {
    value: 'ONEZONE_IA',
    label: 'One Zone – Infrequent Access',
    description: 'Cheapest for rarely accessed files, but stored in only one AWS data centre. Data could be lost if that zone fails.',
    costHint: 'Minimum 30-day storage',
    warning: 'Stored in a single Availability Zone — data could be lost if that zone fails',
  },
  {
    value: 'INTELLIGENT_TIERING',
    label: 'Intelligent Tiering',
    description: 'Automatically moves files between tiers based on how often they are accessed. Monitoring fee per object.',
    costHint: 'Monthly monitoring fee per object',
  },
  {
    value: 'GLACIER_IR',
    label: 'Glacier Instant Retrieval',
    description: 'Archive storage with instant access. 90-day minimum storage. Best for rarely accessed archives.',
    costHint: '90-day minimum storage',
  },
  {
    value: 'GLACIER',
    label: 'Glacier Flexible Retrieval',
    description: 'Cheapest archive. Files take minutes to hours to retrieve. 90-day minimum.',
    costHint: '90-day minimum storage, minutes-to-hours retrieval',
  },
  {
    value: 'DEEP_ARCHIVE',
    label: 'Glacier Deep Archive',
    description: 'Lowest cost. Files take up to 12 hours to retrieve. 180-day minimum storage.',
    costHint: '180-day minimum storage, up to 12-hour retrieval',
  },
] as const;

export const VALID_STORAGE_CLASSES: string[] = STORAGE_CLASSES.map(sc => sc.value);

/**
 * Returns true only for Glacier Flexible Retrieval ('GLACIER') and
 * Glacier Deep Archive ('DEEP_ARCHIVE'). Case-sensitive.
 * Returns false for undefined and all other values.
 */
export function isHardArchiveStorageClass(storageClass: string | undefined): boolean {
  return storageClass === 'GLACIER' || storageClass === 'DEEP_ARCHIVE';
}

/**
 * Returns true only for Glacier Instant Retrieval ('GLACIER_IR').
 * Case-sensitive. Returns false for undefined and all other values.
 */
export function isSoftArchiveStorageClass(storageClass: string | undefined): boolean {
  return storageClass === 'GLACIER_IR';
}

/**
 * Returns true for any Glacier archive storage class (GFR, GDA, or GIR).
 * Equivalent to isHardArchiveStorageClass || isSoftArchiveStorageClass.
 */
export function isAnyArchiveStorageClass(storageClass: string | undefined): boolean {
  return isHardArchiveStorageClass(storageClass) || isSoftArchiveStorageClass(storageClass);
}
